import { useState, useRef, useEffect } from 'react'
import clsx from 'clsx'
import type { TranscriptWord } from '../types'
import WordPopup from './WordPopup'

interface SegmentWordsProps {
  words: TranscriptWord[]
  segmentIndex: number
  onCorrect: (segmentIndex: number, wordIndex: number, correctedTo: string) => void
}

export default function SegmentWords({
  words,
  segmentIndex,
  onCorrect,
}: SegmentWordsProps) {
  const [activeWordIndex, setActiveWordIndex] = useState<number | null>(null)
  const [popupPosition, setPopupPosition] = useState<{ top: number; left: number } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const wordRefs = useRef<(HTMLSpanElement | null)[]>([])

  // Handle word click
  const handleWordClick = (e: React.MouseEvent, wordIndex: number, word: TranscriptWord) => {
    e.stopPropagation()

    // Only show popup for flagged words or corrected words
    const hasFlaggedContent = word.flags && (
      word.flags.low_confidence ||
      word.flags.unknown_term ||
      word.flags.name_variant ||
      word.flags.abbreviation
    )
    const hasSuggestions = word.suggestions && word.suggestions.length > 0
    const isCorrected = !!word.corrected_to

    if (!hasFlaggedContent && !hasSuggestions && !isCorrected) {
      return
    }

    const wordEl = wordRefs.current[wordIndex]
    const containerEl = containerRef.current

    if (wordEl && containerEl) {
      const wordRect = wordEl.getBoundingClientRect()
      const containerRect = containerEl.getBoundingClientRect()

      setPopupPosition({
        top: wordRect.bottom - containerRect.top + 4,
        left: wordRect.left - containerRect.left,
      })
      setActiveWordIndex(wordIndex)
    }
  }

  const handleClosePopup = () => {
    setActiveWordIndex(null)
    setPopupPosition(null)
  }

  // Close popup on scroll
  useEffect(() => {
    const handleScroll = () => handleClosePopup()
    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [])

  const getWordClassName = (word: TranscriptWord, isActive: boolean) => {
    const baseClasses = 'relative rounded px-0.5 -mx-0.5 cursor-pointer transition-colors'

    // Check for flags
    const hasLowConfidence = word.flags?.low_confidence
    const hasUnknownTerm = word.flags?.unknown_term
    const hasNameVariant = word.flags?.name_variant
    const hasAbbreviation = word.flags?.abbreviation
    const isCorrected = !!word.corrected_to
    const hasSuggestions = word.suggestions && word.suggestions.length > 0

    // Word is actionable if it has flags, suggestions, or is corrected
    const isActionable = hasLowConfidence || hasUnknownTerm || hasNameVariant || hasAbbreviation || hasSuggestions || isCorrected

    if (!isActionable) {
      return '' // No special styling for regular words
    }

    if (isActive) {
      return clsx(baseClasses, 'bg-primary-200 ring-2 ring-primary-400')
    }

    if (isCorrected) {
      return clsx(baseClasses, 'bg-green-100 hover:bg-green-200 underline decoration-green-400')
    }

    if (hasLowConfidence) {
      return clsx(baseClasses, 'bg-red-100 hover:bg-red-200 underline decoration-wavy decoration-red-400')
    }

    if (hasUnknownTerm) {
      return clsx(baseClasses, 'bg-amber-100 hover:bg-amber-200 underline decoration-amber-400')
    }

    if (hasNameVariant) {
      return clsx(baseClasses, 'bg-blue-100 hover:bg-blue-200 underline decoration-blue-400')
    }

    if (hasAbbreviation) {
      return clsx(baseClasses, 'bg-purple-100 hover:bg-purple-200')
    }

    if (hasSuggestions) {
      return clsx(baseClasses, 'bg-yellow-100 hover:bg-yellow-200')
    }

    return baseClasses
  }

  return (
    <div ref={containerRef} className="relative leading-relaxed">
      {words.map((word, idx) => {
        const displayText = word.corrected_to || word.text
        const isActive = activeWordIndex === idx
        const className = getWordClassName(word, isActive)

        return (
          <span key={idx}>
            <span
              ref={(el) => { wordRefs.current[idx] = el }}
              className={className}
              onClick={(e) => handleWordClick(e, idx, word)}
              title={word.corrected_to ? `Исходное: "${word.text}"` : undefined}
            >
              {displayText}
            </span>
            {idx < words.length - 1 && ' '}
          </span>
        )
      })}

      {/* Word correction popup */}
      {activeWordIndex !== null && popupPosition && (
        <div
          style={{
            position: 'absolute',
            top: popupPosition.top,
            left: popupPosition.left,
          }}
        >
          <WordPopup
            word={words[activeWordIndex]}
            segmentIndex={segmentIndex}
            wordIndex={activeWordIndex}
            onCorrect={onCorrect}
            onClose={handleClosePopup}
          />
        </div>
      )}
    </div>
  )
}
